import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Menu } from "lucide-react";
export default function HeroSection() {
  const [visible, setVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) setVisible(true);
    }, {
      threshold: 0.1
    });
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);
  return <section ref={sectionRef} className="dark relative min-h-screen bg-background overflow-hidden flex flex-col lg:flex-row">
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,700;0,900;1,700&family=Libre+Baskerville:ital@1&family=Space+Grotesk:wght@300;400;500&display=swap');`}</style>

      <div className="absolute inset-0 z-0 pointer-events-none">
        <div className="absolute top-0 left-0 w-px h-full bg-border opacity-20" style={{
        left: "33.333%"
      }} />
        <div className="absolute top-0 left-0 w-px h-full bg-border opacity-20" style={{
        left: "66.666%"
      }} />
        <div className="absolute top-0 left-0 h-px w-full bg-border opacity-20" style={{
        top: "50%"
      }} />
      </div>

      <div className="relative lg:w-3/5 w-full min-h-[60vh] lg:min-h-screen overflow-hidden" style={{
      clipPath: "polygon(0 0, 100% 0, 92% 100%, 0% 100%)"
    }}>
        <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/985960/img/tortilla-2.jpg" className="w-full h-auto object-cover absolute inset-0 w-full h-full" style={{
        objectPosition: "center center"
      }} alt="Authentic Mexican food" />
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-black/20 to-black/60" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/30" />

        <div className="absolute bottom-8 left-8 flex flex-col gap-1" style={{
        opacity: visible ? 1 : 0,
        transition: "opacity 1.2s ease 0.8s"
      }}>
          {[1, 2, 3].map(i => <div key={i} className="rounded-full bg-white/40" style={{
          width: i === 2 ? "2rem" : "0.5rem",
          height: "0.25rem",
          transition: "all 0.3s ease"
        }} />)}
        </div>

        <div className="absolute top-8 left-8 z-10" style={{
        opacity: visible ? 1 : 0,
        transition: "opacity 0.8s ease 0.2s"
      }}>
          <div className="text-white/90 tracking-[0.4em] uppercase text-xs font-light border border-white/30 px-4 py-2 backdrop-blur-sm" style={{
          fontFamily: "'Space Grotesk', sans-serif"
        }}>
            Authentic Mexican
          </div>
        </div>
      </div>

      <div className="relative lg:w-2/5 w-full flex flex-col justify-center px-10 lg:px-14 xl:px-20 py-16 lg:py-0 bg-background z-10">
        <div className="absolute top-6 right-6 lg:top-10 lg:right-10 w-16 h-16 border border-border opacity-30 rounded-sm" style={{
        transform: "rotate(45deg)"
      }} />
        <div className="absolute bottom-12 right-8 w-24 h-px bg-foreground opacity-10" />

        <div style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(30px)",
        transition: "opacity 0.9s ease 0.4s, transform 0.9s ease 0.4s"
      }}>
          <div className="text-muted-foreground uppercase tracking-[0.5em] text-xs mb-6 flex items-center gap-3" style={{
          fontFamily: "'Space Grotesk', sans-serif"
        }}>
            <span className="inline-block w-8 h-px bg-muted-foreground" />
            Cuisine
          </div>

          <h1 className="text-foreground leading-[1.1] mb-4" style={{
          fontFamily: "'Playfair Display', serif",
          fontSize: "clamp(2.2rem, 4vw, 3.8rem)",
          fontWeight: 900
        }}>
            Along with a variety of other
          </h1>

          <h1 className="leading-[1.1] mb-8" style={{
          fontFamily: "'Playfair Display', serif",
          fontSize: "clamp(2.2rem, 4vw, 3.8rem)",
          fontWeight: 700,
          fontStyle: "italic",
          color: "hsl(var(--muted-foreground))",
          opacity: 0.7
        }}>
            authentic Mexican foods
          </h1>

          <p className="text-muted-foreground text-sm mb-12 max-w-xs leading-relaxed" style={{
          fontFamily: "'Space Grotesk', sans-serif",
          fontWeight: 300
        }}>
            Visit Today
          </p>

          <div className="flex flex-col sm:flex-row gap-4">
            <Button className="group relative overflow-hidden bg-foreground text-background hover:bg-foreground/90 px-8 py-6 text-sm tracking-widest uppercase rounded-none transition-all duration-300" style={{
            fontFamily: "'Space Grotesk', sans-serif",
            letterSpacing: "0.15em"
          }}>
              <span className="relative z-10">Contact Us</span>
              <span className="absolute inset-0 bg-primary scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-300" />
            </Button>

            <Button variant="outline" className="group border border-border text-foreground hover:bg-accent hover:text-accent-foreground px-8 py-6 text-sm tracking-widest uppercase rounded-none transition-all duration-300 bg-transparent" style={{
            fontFamily: "'Space Grotesk', sans-serif",
            letterSpacing: "0.15em"
          }}>
              View Menu
              <span className="ml-3 inline-block transition-transform duration-300 group-hover:translate-x-1">
                &rarr;
              </span>
            </Button>
          </div>

          <div className="mt-16 pt-8 border-t border-border/30">
            <div className="text-muted-foreground text-xs tracking-[0.3em] uppercase" style={{
            fontFamily: "'Space Grotesk', sans-serif"
          }}>
              Handcrafted with tradition
            </div>
          </div>
        </div>
      </div>
    </section>;
}